import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid2, Tab, Tabs, Box, Badge, Alert } from '@mui/material';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

// Hooks & State
import { useModals, MODAL_TYPE } from '../../hooks/useModal';
import { useAuth } from '../../hooks/useAuth';
import { useCapabilities } from '../../hooks/useCapabilities';
import useNotify from '../../hooks/useNotify';
import { useAddShow, useEditShow, useGetShow, getGetShowQueryKey } from '@openapi/shows/shows';

// Components
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import CommonButton from '../../components/atom/CommonButton';
import ShowBasicInfoSection from '../../components/shows/ShowBasicInfoSection';
import ShowMarketingSection from '../../components/shows/ShowMarketingSection';
import ShowSettingsSection from '../../components/shows/ShowSettingsSection';
import ShowStylingSection from '../../components/shows/ShowStylingSection';
import ShowVerificationSection from '../../components/shows/ShowVerificationSection';
import FeesManager from '../../components/shows/FeesManager';
import UpgradePathsSection from './components/UpgradePathsSection';

// Helpers
import { convertFeesToLines, convertLinesToFees } from '../../helpers/feesHelpers';
import { createShowValidationSchema } from './showValidationSchema';

const parseEmailList = (value) => {
    if (!value || typeof value !== 'string') return [];
    const seen = new Set();
    const result = [];
    value
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
        .forEach((email) => {
            const normalized = email.toLowerCase();
            if (!seen.has(normalized)) {
                seen.add(normalized);
                result.push(normalized);
            }
        });
    return result;
};

const DEFAULT_SHOW = (t, venueId) => ({
    title: t('shows.defaultTitle'),
    titleI18n: {},
    subtitle: '',
    subtitleI18n: {},
    description: { type: 'doc', content: [] },
    descriptionI18n: {},
    cancelWindow: 0,
    cancelDescription: { type: 'doc', content: [] },
    cancelDescriptionI18n: {},
    needToKnow: { type: 'doc', content: [] },
    needToKnowI18n: {},
    venueId,
    isIcalEnabled: 0,
    icalUrl: '',
    autoPublishDaysInAdvance: 30,
    enabledAbandonedCartEmail: false,
    reservationTime: 30,
    rollbackTimeSize: 15,
    bookingWindow: null,
    tripadvisorUrl: null,
    countryCode: null,
    destinationCode: null,
    marketing: {
        logo: '',
        logoI18n: {},
        primaryImage: '',
        primaryImageI18n: {},
        extraImages: [],
        extraImagesI18n: {},
        subheadline: '',
        subheadlineI18n: {},
        tags: [],
        tagsI18n: {},
        duration: 0,
        location: '',
        locationI18n: {},
        email: '',
        emailI18n: {},
        phone: '',
        phoneI18n: {},
        tracking: {},
        styling: {},
        delayedSeatAssignmentEnabled: false,
        delayedSeatAssignmentUseSeatIdsInEmails: false,
        allowCrossTicketBuying: false,
        absorbProcessingFee: false,
    },
    waitlistNotificationEmails: '',
    checkinNotificationEmails: '',
    upgradeNotificationEmails: '',
    notificationChannels: ['email'],
    guestCancellationEnabled: true,
    isBreakingOutFees: 1,
    priceListingMode: 'gross',
    verificationScript: '',
    verificationPrompt: '',
    verificationScriptLocked: false,
    verificationMessages: {},
    venues: [],
    venueAvailableLanguages: [],
    reviewText: { type: 'doc', content: [] },
    reviewTextI18n: {},
    reviewLink: '',
    location: '',
    fees: [],
    cancellationReasons: [],
});

function EditShowPage({ newShow }) {
    const { t } = useTranslation();
    const { showId } = useParams();
    const { user } = useAuth();
    const { can, CAPABILITIES } = useCapabilities();
    const { showModal } = useModals();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState(0);

    const numericShowId = useMemo(() => Number(showId), [showId]);
    const isExistingShow = !newShow;
    const defaultShow = useMemo(() => DEFAULT_SHOW(t, user.venueId), [t, user.venueId]);

    // Load show data
    const { data: showData, isLoading: isShowLoading } = useGetShow(numericShowId, {
        query: {
            enabled: isExistingShow && Number.isFinite(numericShowId),
            onError: (error) => {
                notifyError(error);
                navigate('/shows');
            },
        },
    });

    const editShowMutation = useEditShow();
    const addShowMutation = useAddShow();

    // Map show data to form values
    const mappedShow = useMemo(() => {
        if (!showData) return null;
        return {
            id: showData.id,
            title: showData.title ?? '',
            titleI18n: showData.titleI18n ?? {},
            subtitle: showData.subtitle ?? '',
            subtitleI18n: showData.subtitleI18n ?? {},
            description: showData.description ?? defaultShow.description,
            descriptionI18n: showData.descriptionI18n ?? {},
            cancelWindow: showData.cancelWindow ? parseInt(showData.cancelWindow, 10) : 0,
            cancelDescription:
                showData.cancelDescription?.type === 'doc'
                    ? showData.cancelDescription
                    : defaultShow.cancelDescription,
            cancelDescriptionI18n: showData.cancelDescriptionI18n ?? {},
            needToKnow:
                showData.needToKnow?.type === 'doc' ? showData.needToKnow : defaultShow.needToKnow,
            needToKnowI18n: showData.needToKnowI18n ?? {},
            location: showData.location ?? '',
            venueId: showData.venueId ?? defaultShow.venueId,
            venueName: showData.venueName,
            venueAvailableLanguages: showData.venueAvailableLanguages ?? [],
            isIcalEnabled: showData.timeslotMgmt === 'ical' ? 1 : 0,
            icalUrl: showData.timeslotMgmtOptions?.url ?? '',
            autoPublishDaysInAdvance:
                showData.timeslotMgmtOptions?.autoPublishDays ??
                defaultShow.autoPublishDaysInAdvance,
            reviewText:
                showData.reviewText?.type === 'doc' ? showData.reviewText : defaultShow.reviewText,
            reviewTextI18n: showData.reviewTextI18n ?? {},
            reviewLink: showData.reviewLink ?? '',
            enabledAbandonedCartEmail: Boolean(showData.enabledAbandonedCartEmail),
            reservationTime: showData.reservationTime ?? defaultShow.reservationTime,
            rollbackTimeSize: showData.rollbackTimeSize ?? defaultShow.rollbackTimeSize,
            bookingWindow:
                showData.bookingWindow !== undefined
                    ? showData.bookingWindow
                    : defaultShow.bookingWindow,
            tripadvisorUrl: showData.tripadvisorUrl ?? null,
            countryCode: showData.countryCode ?? null,
            destinationCode: showData.destinationCode ?? null,
            marketing: {
                ...defaultShow.marketing,
                ...(showData.marketing ?? {}),
                extraImages: showData.marketing?.extraImages ?? [],
                extraImagesI18n: showData.marketing?.extraImagesI18n ?? {},
                tags: showData.marketing?.tags ?? [],
                tagsI18n: showData.marketing?.tagsI18n ?? {},
            },
            waitlistNotificationEmails: (showData.waitlistNotificationEmails ?? []).join(', '),
            checkinNotificationEmails: (showData.checkinNotificationEmails ?? []).join(', '),
            upgradeNotificationEmails: (showData.upgradeNotificationEmails ?? []).join(', '),
            notificationChannels: showData.notificationChannels ?? defaultShow.notificationChannels,
            guestCancellationEnabled: showData.guestCancellationEnabled !== false,
            cancellationReasons: (showData.cancellationReasons ?? []).map((r) => ({
                ...r,
                _key: crypto.randomUUID(),
            })),
            isBreakingOutFees: showData.isBreakingOutFees ?? defaultShow.isBreakingOutFees,
            priceListingMode: showData.priceListingMode ?? defaultShow.priceListingMode,
            activeArrangementId: showData.activeArrangementId ?? null,
            verificationScript: showData.verificationScript ?? '',
            verificationPrompt: showData.verificationPrompt ?? '',
            verificationScriptLocked: !!showData.verificationScriptLocked,
            verificationMessages: showData.verificationMessages ?? {},
            venues: showData.venues ?? [],
            fees: convertFeesToLines(showData.fees),
        };
    }, [showData, defaultShow]);

    // Submit handlers
    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editShowMutation.mutateAsync({
                        id: numericShowId,
                        data: {
                            ...values,
                            timeslotMgmt: values.isIcalEnabled ? 'ical' : null,
                            timeslotMgmtOptions: values.icalUrl
                                ? {
                                      url: values.icalUrl,
                                      autoPublishDays: values.autoPublishDaysInAdvance,
                                  }
                                : null,
                            fees: convertLinesToFees(values.fees),
                            waitlistNotificationEmails: parseEmailList(
                                values.waitlistNotificationEmails,
                            ),
                            checkinNotificationEmails: parseEmailList(
                                values.checkinNotificationEmails,
                            ),
                            upgradeNotificationEmails: parseEmailList(
                                values.upgradeNotificationEmails,
                            ),
                        },
                    });
                    await queryClient.invalidateQueries({
                        queryKey: getGetShowQueryKey(numericShowId),
                    });
                    notifySuccess(t('shows.notifications.updated'));
                    await queryClient.invalidateQueries({ queryKey: ['/shows'] });
                    navigate('/shows');
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values, { setSubmitting }) => {
        if (!newShow) {
            onEdit(values);
            setSubmitting(false);
            return;
        }
        try {
            await addShowMutation.mutateAsync({
                data: {
                    ...values,
                    timeslotMgmt: values.isIcalEnabled ? 'ical' : null,
                    timeslotMgmtOptions: {
                        url: values.icalUrl,
                        autoPublishDays: values.autoPublishDaysInAdvance,
                    },
                    fees: convertLinesToFees(values.fees),
                    waitlistNotificationEmails: parseEmailList(values.waitlistNotificationEmails),
                    checkinNotificationEmails: parseEmailList(values.checkinNotificationEmails),
                    upgradeNotificationEmails: parseEmailList(values.upgradeNotificationEmails),
                },
            });
            await queryClient.invalidateQueries({ queryKey: ['/shows'] });
            navigate('/shows');
            notifySuccess(t('shows.notifications.added'));
        } catch (error) {
            notifyError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const initialValues = useMemo(() => {
        const base = newShow ? defaultShow : (mappedShow ?? defaultShow);
        return {
            ...base,
            marketing: {
                ...defaultShow.marketing,
                ...(base.marketing ?? {}),
            },
            venues: base.venues ?? [],
            venueAvailableLanguages: base.venueAvailableLanguages ?? [],
        };
    }, [newShow, defaultShow, mappedShow]);

    if (isExistingShow && (isShowLoading || !mappedShow)) {
        return true;
    }

    // Helper to check if a tab has errors
    const getTabErrors = (errors, touched) => {
        const tabErrors = [
            // Tab 0: Basic Info
            [
                errors.title && touched.title,
                errors.subtitle && touched.subtitle,
                errors.marketing?.subheadline && touched.marketing?.subheadline,
            ].filter(Boolean).length,
            // Tab 1: Marketing
            [
                errors.description && touched.description,
                errors.marketing?.duration && touched.marketing?.duration,
                errors.marketing?.location && touched.marketing?.location,
                errors.marketing?.email && touched.marketing?.email,
                errors.marketing?.emailI18n && touched.marketing?.emailI18n,
                errors.marketing?.phone && touched.marketing?.phone,
            ].filter(Boolean).length,
            // Tab 2: Settings
            [
                errors.venueId && touched.venueId,
                errors.location && touched.location,
                errors.cancelWindow && touched.cancelWindow,
                errors.reservationTime && touched.reservationTime,
                errors.rollbackTimeSize && touched.rollbackTimeSize,
                errors.bookingWindow && touched.bookingWindow,
                errors.cancelDescription && touched.cancelDescription,
                errors.needToKnow && touched.needToKnow,
                errors.reviewText && touched.reviewText,
                errors.reviewLink && touched.reviewLink,
                errors.tripadvisorUrl && touched.tripadvisorUrl,
                errors.icalUrl && touched.icalUrl,
                errors.autoPublishDaysInAdvance && touched.autoPublishDaysInAdvance,
                errors.waitlistNotificationEmails && touched.waitlistNotificationEmails,
                errors.checkinNotificationEmails && touched.checkinNotificationEmails,
            ].filter(Boolean).length,
            // Tab 3: Verification (no Formik validation; codes/messages are
            // free-form and surface server-side errors at runtime)
            0,
            // Tab 4: Fees
            errors.fees ? (Array.isArray(errors.fees) ? errors.fees.filter(Boolean).length : 1) : 0,
            // Tab 5: Styling
            0,
        ];
        return tabErrors;
    };

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={createShowValidationSchema(t)}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => {
                const tabErrors = getTabErrors(errors, touched);
                const totalErrors = tabErrors.reduce((sum, count) => sum + count, 0);

                return (
                    <Form>
                        <PageWithTitle title={newShow ? t('shows.newShowTitle') : values.title}>
                            <Grid2 size={{ xs: 12 }}>
                                <BlockWithTitle>
                                    {/* Error Summary */}
                                    {totalErrors > 0 && (
                                        <Grid2 size={{ xs: 12 }} sx={{ mb: 2 }}>
                                            <Alert severity="error">
                                                {t('common.formHasErrors', {
                                                    count: totalErrors,
                                                    defaultValue: `Please fix ${totalErrors} error(s) before saving`,
                                                })}
                                            </Alert>
                                        </Grid2>
                                    )}

                                    {/* Tabs and Buttons */}
                                    <Grid2 size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                mb: 3,
                                                display: 'flex',
                                                flexDirection: { xs: 'column', md: 'row' },
                                                justifyContent: 'space-between',
                                                alignItems: { xs: 'stretch', md: 'center' },
                                                gap: { xs: 1, md: 0 },
                                                mt: -2,
                                            }}
                                        >
                                            <Tabs
                                                value={activeTab}
                                                onChange={(_, newValue) => setActiveTab(newValue)}
                                                variant="scrollable"
                                                scrollButtons
                                                allowScrollButtonsMobile
                                                sx={{
                                                    minWidth: 0,
                                                    // Keep both scroll arrows visible; dim the
                                                    // inactive one instead of hiding it.
                                                    '& .MuiTabs-scrollButtons.Mui-disabled': {
                                                        opacity: 0.3,
                                                    },
                                                    '& .MuiTab-root': {
                                                        textTransform: 'none',
                                                        fontSize: '1.1rem',
                                                        fontWeight: 500,
                                                        minHeight: '48px',
                                                        paddingRight: '32px',
                                                    },
                                                }}
                                            >
                                                <Tab
                                                    label={
                                                        <Badge
                                                            badgeContent={tabErrors[0]}
                                                            color="error"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    right: -20,
                                                                },
                                                            }}
                                                        >
                                                            {t('shows.mainInfoTitle')}
                                                        </Badge>
                                                    }
                                                />
                                                <Tab
                                                    label={
                                                        <Badge
                                                            badgeContent={tabErrors[1]}
                                                            color="error"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    right: -20,
                                                                },
                                                            }}
                                                        >
                                                            {t('shows.detailsTitle')}
                                                        </Badge>
                                                    }
                                                />
                                                <Tab
                                                    label={
                                                        <Badge
                                                            badgeContent={tabErrors[2]}
                                                            color="error"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    right: -20,
                                                                },
                                                            }}
                                                        >
                                                            {t('shows.settingsTitle')}
                                                        </Badge>
                                                    }
                                                />
                                                <Tab
                                                    label={
                                                        <Badge
                                                            badgeContent={tabErrors[3]}
                                                            color="error"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    right: -20,
                                                                },
                                                            }}
                                                        >
                                                            {t('shows.verification.tabTitle')}
                                                        </Badge>
                                                    }
                                                />
                                                <Tab
                                                    label={
                                                        <Badge
                                                            badgeContent={tabErrors[4]}
                                                            color="error"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    right: -20,
                                                                },
                                                            }}
                                                        >
                                                            {t('shows.feesTitle')}
                                                        </Badge>
                                                    }
                                                />
                                                <Tab
                                                    label={
                                                        <Badge
                                                            badgeContent={tabErrors[5]}
                                                            color="error"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    right: -20,
                                                                },
                                                            }}
                                                        >
                                                            {t('shows.stylingTabTitle')}
                                                        </Badge>
                                                    }
                                                />
                                                {isExistingShow && (
                                                    <Tab label={t('upgrades.upgradePaths')} />
                                                )}
                                            </Tabs>

                                            {/* Action Buttons */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 2,
                                                    flexShrink: 0,
                                                    width: { xs: '100%', md: 'auto' },
                                                    pb: { xs: 1, md: 0 },
                                                }}
                                            >
                                                <CommonButton
                                                    onClick={() => navigate('/shows')}
                                                    label={t('common.cancel')}
                                                    buttonType="secondary"
                                                    sx={{ flex: { xs: 1, md: 'none' } }}
                                                />
                                                <CommonButton
                                                    disabled={
                                                        isSubmitting ||
                                                        addShowMutation.isPending ||
                                                        editShowMutation.isPending
                                                    }
                                                    type="submit"
                                                    label={t('common.save')}
                                                    buttonType="primary"
                                                    sx={{ flex: { xs: 1, md: 'none' } }}
                                                />
                                            </Box>
                                        </Box>
                                    </Grid2>

                                    {/* Tab Panel: Basic Info */}
                                    {activeTab === 0 && (
                                        <ShowBasicInfoSection
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            showModal={showModal}
                                        />
                                    )}

                                    {/* Tab Panel: Marketing */}
                                    {activeTab === 1 && (
                                        <ShowMarketingSection
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            showModal={showModal}
                                        />
                                    )}

                                    {/* Tab Panel: Settings */}
                                    {activeTab === 2 && (
                                        <ShowSettingsSection
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            canManageVenues={can(CAPABILITIES.VENUES_MANAGE)}
                                            showModal={showModal}
                                            showId={numericShowId}
                                        />
                                    )}

                                    {/* Tab Panel: Verification */}
                                    {activeTab === 3 && (
                                        <ShowVerificationSection
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            showId={numericShowId}
                                            showModal={showModal}
                                            notifyError={notifyError}
                                            notifySuccess={notifySuccess}
                                        />
                                    )}

                                    {/* Tab Panel: Fees */}
                                    {activeTab === 4 && (
                                        <FeesManager
                                            fees={values.fees}
                                            onChange={(fees) => setFieldValue('fees', fees)}
                                            venueId={values.venueId}
                                            errors={errors.fees}
                                        />
                                    )}

                                    {/* Tab Panel: Styling */}
                                    {activeTab === 5 && (
                                        <ShowStylingSection
                                            values={values}
                                            setFieldValue={setFieldValue}
                                        />
                                    )}

                                    {/* Tab Panel: Upgrade Paths */}
                                    {isExistingShow && activeTab === 6 && (
                                        <UpgradePathsSection showId={numericShowId} />
                                    )}
                                </BlockWithTitle>
                            </Grid2>
                        </PageWithTitle>
                    </Form>
                );
            }}
        </Formik>
    );
}

export default EditShowPage;
